const libPictView = require('pict-view');
const { v4: uuidv4 } = require('uuid');

/** @type {any} */
const _ViewConfiguration = require('./panes/HeadlightPanels-Pane-View-Templates.json')['HeadlightPanels-Pane-ValidationsPaneHtml-View'];

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

/**
 * @typedef {Object} SeverityTreatment
 * @property {string} Label             - Human-readable label (e.g. "Warning").
 * @property {string} IconClass         - FontAwesome icon classes (e.g. "fas fa-fw fa-triangle-exclamation").
 * @property {string} AccentClass       - Bulma color modifier applied to the panel-block (e.g. "is-warning").
 * @property {string} TextClass         - Bulma text-color helper (e.g. "has-text-warning-dark").
 * @property {string} BackgroundClass   - Bulma background-tone helper (e.g. "has-background-warning-light").
 * @property {string} BorderColorVar    - CSS variable used for the left accent border (e.g. "var(--bulma-warning)").
 */

/**
 * @typedef {Object} LogInput
 * @property {string} Message
 * @property {string=} Severity         - One of the keys in the severity map (info, warning, error, success, debug, ...). Case-insensitive.
 * @property {function=} onClickHandler
 */

/**
 * @typedef {Object} ValidationLogEntry
 * @property {string} Key
 * @property {string} Log
 * @property {string} Severity
 * @property {function=} onClickHandler
 */

/**
 * @typedef {Object} ValidationsSection
 * @property {string} Key
 * @property {string} Name
 * @property {ValidationLogEntry[]} Logs
 * @property {boolean=} Collapsed              - Persistent collapse state, flipped by toggleSectionCollapse.
 * @property {string=} ToggleKey               - Computed in onBeforeRenderAsync; identifies the collapsible group in DOM data attributes.
 * @property {string=} HiddenClass             - Computed in onBeforeRenderAsync; 'is-hidden' when Collapsed, '' otherwise.
 * @property {string=} ChevronIconClass        - Computed in onBeforeRenderAsync; 'fa-chevron-right' when Collapsed, 'fa-chevron-down' otherwise.
 */

/**
 * Default severity treatments. Forms can extend or override these via
 * `registerSeverityTreatments(...)` instead of editing this file.
 *
 * @type {Record<string, SeverityTreatment>}
 */
const DEFAULT_SEVERITY_MAP =
{
	info:
	{
		Label: 'Info',
		IconClass: 'fas fa-fw fa-circle-info',
		AccentClass: 'is-info',
		TextClass: 'has-text-info-light',
		BackgroundClass: 'has-background-black-ter',
		BorderColorVar: 'var(--bulma-info-dark)',
	},
	success:
	{
		Label: 'Success',
		IconClass: 'fas fa-fw fa-circle-check',
		AccentClass: 'is-success',
		TextClass: 'has-text-success-light',
		BackgroundClass: 'has-background-black-ter',
		BorderColorVar: 'var(--bulma-success-dark)',
	},
	warning:
	{
		Label: 'Warning',
		IconClass: 'fas fa-fw fa-triangle-exclamation',
		AccentClass: 'is-warning',
		TextClass: 'has-text-warning-light',
		BackgroundClass: 'has-background-black-ter',
		BorderColorVar: 'var(--bulma-warning-dark)',
	},
	error:
	{
		Label: 'Error',
		IconClass: 'fas fa-fw fa-circle-exclamation',
		AccentClass: 'is-danger',
		TextClass: 'has-text-danger-light',
		BackgroundClass: 'has-background-black-ter',
		BorderColorVar: 'var(--bulma-danger-dark)',
	},
	debug:
	{
		Label: 'Debug',
		IconClass: 'fas fa-fw fa-bug',
		AccentClass: 'is-dark',
		TextClass: 'has-text-grey-lighter',
		BackgroundClass: 'has-background-black-ter',
		BorderColorVar: 'var(--bulma-grey-dark)',
	},
	default:
	{
		Label: 'Note',
		IconClass: 'fas fa-fw fa-circle-dot',
		AccentClass: 'is-dark',
		TextClass: 'has-text-light',
		BackgroundClass: 'has-background-black-ter',
		BorderColorVar: 'var(--bulma-grey-light)',
	},
};

class HeadlightPaneValidations extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _ViewConfiguration, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		// we'll need to keep track of logs here for re-rendering the pane
		this.HeadlightPaneValidationLog = [];
		/** @type {ValidationsSection[]} */
		this.HeadlightPaneValidationSections = [];

		// Severity map is per-instance so callers can layer in form-specific treatments
		// without leaking into other apps that share this view module. Deep-copy each
		// treatment so registerSeverityTreatments() can mutate fields safely.
		/** @type {Record<string, SeverityTreatment>} */
		this._severityMap = {};
		for (const key of Object.keys(DEFAULT_SEVERITY_MAP))
		{
			this._severityMap[key] = Object.assign({}, DEFAULT_SEVERITY_MAP[key]);
		}
	}

	/**
	 * Merge custom severity treatments into the active severity map. Each entry in
	 * `pCustomMap` is shallow-merged onto the existing treatment for that key, so
	 * callers can override just `IconClass` (for example) without re-stating every
	 * other field. New keys are added wholesale.
	 *
	 * @param {Record<string, Partial<SeverityTreatment>>} pCustomMap
	 */
	registerSeverityTreatments(pCustomMap)
	{
		if (!pCustomMap || typeof pCustomMap !== 'object')
		{
			return;
		}
		for (const rawKey of Object.keys(pCustomMap))
		{
			const key = String(rawKey).toLowerCase();
			const existing = this._severityMap[key] || this._severityMap.default;
			this._severityMap[key] = Object.assign({}, existing, pCustomMap[rawKey]);
		}
	}

	/**
	 * Resolve a severity string (case-insensitive) to a treatment, falling back to
	 * the `default` treatment if the key is missing.
	 *
	 * @param {string=} pSeverity
	 * @return {SeverityTreatment & { Key: string }}
	 */
	getSeverityTreatment(pSeverity)
	{
		const key = (pSeverity ? String(pSeverity).toLowerCase() : 'default');
		const treatment = this._severityMap[key] || this._severityMap.default;
		return Object.assign({ Key: this._severityMap[key] ? key : 'default' }, treatment);
	}

	/**
	 * @return {ValidationsSection[]}
	 */
	get sections()
	{
		return this.HeadlightPaneValidationSections;
	}

	/**
	 * @return {ValidationLogEntry[]}
	 */
	get validations()
	{
		return this.sections.map((s) => s.Logs).flat();
	}

	/**
	 *
	 * @param {string} sectionKey
	 * @return {ValidationsSection | undefined}
	 */
	getSection(sectionKey)
	{
		return this.sections.find((s) => s.Key === sectionKey);
	}

	indexOfSection(sectionKey)
	{
		return this.sections.findIndex((s) => s.Key === sectionKey);
	}

	/**
	 *
	 * @param {string} logKey
	 * @return {ValidationLogEntry | undefined}
	 */
	getLogEntry(logKey)
	{
		return this.validations.find((l) => l.Key === logKey);
	}

	/**
	 * @param {LogInput} logInput
	 * @return {ValidationLogEntry}
	 */
	generateLogEntry(logInput)
	{
		const treatment = this.getSeverityTreatment(logInput.Severity);
		const key = uuidv4();
		// Row-style entry: full-width with an 8px leading border whose
		// color is driven by --hl-log-row-accent-color (panels-logs.scss).
		// We set the var inline from `treatment.BorderColorVar` so custom
		// severities registered via registerSeverityTreatments() pick up
		// their own accent color end-to-end. The `severity-${Key}` and
		// Bulma `is-*` classes provide the same fallback for built-in
		// severities, and `treatment.AccentClass` stays on the row so
		// existing callers/tests that look for it still match.
		// `BackgroundClass` is intentionally NOT applied — the panel's
		// dark surface stays clean and the leading border carries the
		// severity tone. `TextClass` styles the label line only.
		const log = /*html*/`
		<div class="hl-log-row severity-${treatment.Key} ${treatment.AccentClass} is-clickable"
			style="--hl-log-row-accent-color: ${treatment.BorderColorVar};"
			onclick="_Pict.views.HeadlightPaneValidations.onClickEntry('${key}')">
			<p class="hl-log-row-label ${treatment.TextClass}">
				<span class="icon"><i class="${treatment.IconClass}" aria-hidden="true"></i></span>
				<span>${treatment.Label}</span>
			</p>
			<p class="hl-log-row-message">${logInput.Message}</p>
		</div>`;
		return {
			Key: key,
			Log: log,
			Severity: treatment.Key,
			onClickHandler: logInput.onClickHandler,
		};
	}

	/**
	 * Normalize the variadic / array message arguments accepted by addLogSection
	 * and addToLogSection. Allows callers to use any of:
	 *   - a single LogInput object: fn(key, { Message: 'x' })
	 *   - spread args:               fn(key, msg1, msg2, msg3)
	 *   - a single array:            fn(key, [msg1, msg2])
	 *
	 * @param {(LogInput|LogInput[])[]} pRestArgs - The rest-collected `...messages` argument.
	 * @return {LogInput[]}
	 * @private
	 */
	_normalizeMessages(pRestArgs)
	{
		if (pRestArgs.length === 1 && Array.isArray(pRestArgs[0]))
		{
			return pRestArgs[0];
		}
		return /** @type {LogInput[]} */ (pRestArgs);
	}

	/**
	 * Append messages to a section's existing logs (creates the section if it doesn't exist).
	 *
	 * @param {string} sectionKey
	 * @param {...(LogInput|LogInput[])} messages - One or more LogInput objects, spread or as an array.
	 */
	addToLogSection(sectionKey, ...messages)
	{
		const list = this._normalizeMessages(messages);
		let section = this.getSection(sectionKey);
		if (!section)
		{
			section = { Key: sectionKey, Name: sectionKey, Logs: [] };
			// Append new sections so they line up with the order the form's
			// validators registered them (and, by extension, the visual order
			// of the form content itself).
			this.sections.push(section);
		}
		const newLogEntries = list.map((m) => this.generateLogEntry(m));
		// Append within the section too so logs read top-down in the order
		// the validator emitted them.
		section.Logs.push(...newLogEntries);

		this.pict.views.HeadlightPanels.updateNavButtonBadge('Validations', this.validations.length);
		this.render();
	}

	/**
	 * Replace a section's logs with a new set (creates the section if it doesn't exist).
	 *
	 * @param {string} sectionKey
	 * @param {...(LogInput|LogInput[])} messages - One or more LogInput objects, spread or as an array.
	 */
	addLogSection(sectionKey, ...messages)
	{
		const list = this._normalizeMessages(messages);
		const newLogEntries = list.map((m) => this.generateLogEntry(m));
		const existingIndex = this.indexOfSection(sectionKey);
		if (existingIndex >= 0)
		{
			this.sections[existingIndex].Logs = newLogEntries;
		}
		else
		{
			// Append new sections so they line up with the order the form's
			// validators registered them.
			this.sections.push({ Key: sectionKey, Name: sectionKey, Logs: newLogEntries });
		}

		this.pict.views.HeadlightPanels.updateNavButtonBadge('Validations', this.validations.length);
		this.render();
	}

	/**
	 *
	 * @param {string} sectionKey
	 */
	clearSection(sectionKey)
	{
		const index = this.indexOfSection(sectionKey);
		if (index < 0)
		{
			return;
		}
		this.sections.splice(index, 1);

		this.pict.views.HeadlightPanels.updateNavButtonBadge('Validations', this.validations.length);
		this.render();
	}

	generateFlatLogsList()
	{
		this.HeadlightPaneValidationLog = this.validations;
	}

	/**
	 * Compute the data-attribute toggle key used to wire each section's header
	 * to `toggleCollapsiblePanelBlockGroup`. Prefixed so it can't collide with
	 * other collapsibles in the panel pane.
	 *
	 * @param {string} sectionKey
	 * @return {string}
	 * @private
	 */
	_getSectionToggleKey(sectionKey)
	{
		return `ValidationsSection-${sectionKey}`;
	}

	/**
	 * Flip the collapsed state for a section. Updates the persistent
	 * `Collapsed` flag on the section data so the state survives the next
	 * re-render, and toggles the DOM directly via the existing
	 * `toggleCollapsiblePanelBlockGroup` helper for snappy UX.
	 *
	 * @param {string} sectionKey
	 */
	toggleSectionCollapse(sectionKey)
	{
		const section = this.getSection(sectionKey);
		if (!section)
		{
			return;
		}
		section.Collapsed = !section.Collapsed;
		this.pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup(this._getSectionToggleKey(sectionKey));
	}

	/**
	 * Lifecycle hook that runs ahead of every render — both sync `render()`
	 * and `renderAsync()` flows route through this (the base class's
	 * onBeforeRenderAsync delegates to onBeforeRender). Recomputes the flat
	 * log list used by the empty-state conditional and pre-computes
	 * per-section presentation strings for the section template.
	 *
	 * @param {any} _pRenderable
	 * @return {boolean}
	 */
	onBeforeRender(_pRenderable)
	{
		this.generateFlatLogsList();
		for (const section of this.sections)
		{
			section.ToggleKey = this._getSectionToggleKey(section.Key);
			section.HiddenClass = section.Collapsed ? 'is-hidden' : '';
			section.ChevronIconClass = section.Collapsed ? 'fa-chevron-right' : 'fa-chevron-down';
		}
		return true;
	}

	/**
	 *
	 * @param {string} logKey
	 */
	onClickEntry(logKey)
	{
		this.pict.log.debug(`[Pane-Validations] onClickEntry logKey[${logKey}]`);
		const logEntry = this.getLogEntry(logKey);
		if (!logEntry)
		{
			this.pict.log.error(`[onClickEntry] logKey[${logKey}] is missing`);
			return;
		}
		if (typeof(logEntry.onClickHandler) === 'function')
		{
			this.pict.log.debug(`[Pane-Validations] onClickEntry logKey[${logKey}] calling onClickHandler()`);
			logEntry.onClickHandler();
			return;
		}
		// default behavior: highlight source dom element and shift focus to it
	}
}

module.exports = HeadlightPaneValidations;
module.exports.DEFAULT_SEVERITY_MAP = DEFAULT_SEVERITY_MAP;

// additional view-specific templates
const viewSpecificTemplates = [
	{
		Hash: 'HeadlightPanels-ValidationPanel-Content',
		Template: /*html*/`{~TS:HeadlightPanels-ValidationPanel-Content-Section:Pict.views.HeadlightPaneValidations.HeadlightPaneValidationSections~}`,
	},
	{
		Hash: 'HeadlightPanels-ValidationPanel-Content-Section',
		Template: /*html*/`
		<div class="hl-log-section">
			<div class="hl-log-section-header" data-collapsible-toggle="{~D:Record.ToggleKey~}" onclick="_Pict.views.HeadlightPaneValidations.toggleSectionCollapse('{~D:Record.Key~}')">
				<span class="hl-log-section-icon icon">
					<i class="fas fa-fw fa-list-check" aria-hidden="true"></i>
				</span>
				<span class="hl-log-section-name">{~D:Record.Name~}</span>
				<span class="hl-log-section-badge">{~D:Record.Logs.length~}</span>
				<span class="hl-log-section-chevron icon">
					<i class="fas fa-fw {~D:Record.ChevronIconClass~}" aria-hidden="true"></i>
				</span>
			</div>
			<div data-collapsible-content="{~D:Record.ToggleKey~}" class="hl-log-section-body {~D:Record.HiddenClass~}">
				{~TS:HeadlightPanels-ValidationPanel-Content-LogEntry:Record.Logs~}
			</div>
		</div>
		`,
	},
	{
		Hash: 'HeadlightPanels-ValidationPanel-Content-LogEntry',
		Template: /*html*/`{~D:Record.Log~}`,
	},
	{
		Hash: 'HeadlightPanels-ValidationPanel-EmptyMessage',
		Template: /*html*/`
		<div class="hl-log-row severity-success is-success" style="border-left-width: 5px; padding: 0.85rem 1rem; text-align: center;">
			<p class="hl-log-row-label has-text-success-light is-justify-content-center" style="font-size: 0.85rem;">
				<span class="icon"><i class="fas fa-fw fa-circle-check" aria-hidden="true"></i></span>
				<span>All clear</span>
			</p>
			<p class="hl-log-row-message">No validation issues to address right now.</p>
		</div>`,
	},
];

// append the view-specific templates to the global templates that are generated from the JSON file
_ViewConfiguration.Templates = [ ..._ViewConfiguration.Templates, ...viewSpecificTemplates ];
module.exports.default_configuration = _ViewConfiguration;
